import { ContainerSizeValue } from '../../../directives/container-size/container-size.directive';
import { QuoteTab } from './domain/quote-chart.domain';
import {
  QuoteTimeRange,
  QuoteTimePeriod,
  QuoteIssueType
} from './domain';
import { QuoteLink } from './quote-link/quote-link.component';

export interface TimeRangeSelector {
  type: QuoteTimeRange;
  chartSeries: QuoteTimeRange;
  frequencies: string;
  translationId: string;
}

export interface ChartTypeSelector {
  type: QuoteChartType;
  translationId: string;
}

export interface FrequencySelector {
  timePeriod?: QuoteTimePeriod;
  interval?: number;
  translationId?: string;
}

export interface QuoteLinks {
  title: string;
  link: string;
  symbolFrom?: 'underlyingSymbol' | 'issueId';
}

export enum QuoteChartType {
  LINE = 'line',
  CANDLESTICK = 'candlestick',
  AREA = 'area'
}

export enum QuoteError {
  AGREEMENT_DECLINED = 'QUOTE.ERROR.AGREEMENT_DECLINED',
  SYMBOL_NOT_FOUND = 'QUOTE.ERROR.SYMBOL_NOT_FOUND',
  SYMBOL_INVALID = 'QUOTE.ERROR.SYMBOL_INVALID'
}

/**
 * Constants for Quote component
 */
export class QuoteConstants {

  static readonly containerSizeValue: ContainerSizeValue = {
    small: 737,
    medium: 993,
    large: 1200
  };

  static readonly LOCAL_STORAGE_KEY: string = 'ExpandedQuoteTab';
  static readonly WATCHLIST: string = 'WATCHLIST';
  static readonly HOLDINGS: string = 'HOLDINGS';
  static readonly SUMMARY_TAB: string = 'SUMMARY';
  static readonly MORE_DETAILS_TAB: string = 'MORE_DETAILS';

  static readonly CURRENT_TAB_DEFAULT_DAILY: QuoteTab = {
    type: 'default',
    chartSeries: 'daily',
    translationId: 'tdWb.markets.quote.quotecharts.TODAYS_RANGE'
  };

  static readonly CURRENT_TAB_DEFAULT_MONTHLY: QuoteTab = {
    type: 'default',
    chartSeries: 'monthly',
    translationId: 'tdWb.markets.quote.quotecharts.THIS_MONTH'
  };

  static readonly CURRENT_TAB_MONTHLY: QuoteTab = {
    type: 'monthly',
    chartSeries: 'monthly',
    translationId: 'tdWb.markets.quote.quotecharts.THIS_MONTH'
  };

  static readonly CURRENT_TAB_3MONTHS: QuoteTab = {
    type: 'three-months',
    chartSeries: 'three-months',
    translationId: 'tdWb.markets.quote.quotecharts.THREE_MONTHS'
  };

  static readonly CURRENT_TAB_YEARLY: QuoteTab = {
    type: 'yearly',
    chartSeries: 'yearly',
    translationId: 'tdWb.markets.quote.quotecharts.FIFTY_TWO_WEEK_RANGE'
  };

  static readonly TABS_DEFAULT: Array<QuoteTab> = [
    QuoteConstants.CURRENT_TAB_DEFAULT_DAILY,
    QuoteConstants.CURRENT_TAB_MONTHLY,
    QuoteConstants.CURRENT_TAB_YEARLY
  ];

  static readonly TABS_MF: Array<QuoteTab> = [
    QuoteConstants.CURRENT_TAB_DEFAULT_MONTHLY,
    QuoteConstants.CURRENT_TAB_3MONTHS,
    QuoteConstants.CURRENT_TAB_YEARLY
  ];

  static readonly CHART_TYPE_SELECTOR: Array<ChartTypeSelector> = [
    { type: QuoteChartType.LINE, translationId: 'tdWb.markets.quote.chartType.LINE' },
    { type: QuoteChartType.CANDLESTICK, translationId: 'tdWb.markets.quote.chartType.CANDLESTICK' },
    { type: QuoteChartType.AREA, translationId: 'tdWb.markets.quote.chartType.AREA' }
  ];

  static readonly mapData: unknown = {
    line: (dataSeries: Array<Array<unknown>>): unknown => dataSeries.map(function(item: Array<unknown>): Array<unknown> {
      return [item[0], item[5]];
    }),
    area: (dataSeries: Array<Array<unknown>>): unknown => dataSeries.map(function(item: Array<unknown>): Array<unknown> {
      return [item[0], item[5]];
    }),
    candlestick: (dataSeries: Array<Array<unknown>>): unknown => dataSeries.map(function(item: Array<unknown>): Array<unknown> {
      return [item[0], item[2], item[3], item[4], item[5]];
    }),
    volume: (dataSeries: Array<Array<unknown>>): unknown => dataSeries.map(function(item: Array<unknown>): Array<unknown> {
      return [item[0], item[1]];
    })
  };

  static readonly DEFAULT_TIME_RANGE_SELECTOR:
  { mutualFund: Array<TimeRangeSelector>; others: Array<TimeRangeSelector> } = {
      mutualFund: [
        { type: QuoteTimeRange.MONTHLY, chartSeries: QuoteTimeRange.MONTHLY,
          frequencies: 'WEEKLY', translationId: 'tdWb.markets.quote.timePeriod.MONTHLY' },
        { type: QuoteTimeRange.THREE_MONTHS, chartSeries: QuoteTimeRange.THREE_MONTHS,
          frequencies: 'WEEKLY', translationId: 'tdWb.markets.quote.timePeriod.THREE_MONTHS' },
        { type: QuoteTimeRange.YEAR_TO_DATE, chartSeries: QuoteTimeRange.YEARLY,
          frequencies: 'WEEKLY', translationId: 'tdWb.markets.quote.timePeriod.YEAR_TO_DATE' },
        { type: QuoteTimeRange.YEARLY, chartSeries: QuoteTimeRange.YEARLY,
          frequencies: 'MONTHLY', translationId: 'tdWb.markets.quote.timePeriod.YEARLY' },
        { type: QuoteTimeRange.THREE_YEARS, chartSeries: QuoteTimeRange.THREE_YEARS,
          frequencies: 'MONTHLY', translationId: 'tdWb.markets.quote.timePeriod.THREE_YEARS' },
        { type: QuoteTimeRange.FIVE_YEARS, chartSeries: QuoteTimeRange.FIVE_YEARS,
          frequencies: 'MONTHLY', translationId: 'tdWb.markets.quote.timePeriod.FIVE_YEARS' },
        { type: QuoteTimeRange.TEN_YEARS, chartSeries: QuoteTimeRange.TEN_YEARS,
          frequencies: 'MONTHLY', translationId: 'tdWb.markets.quote.timePeriod.TEN_YEARS' },
        { type: QuoteTimeRange.TWENTY_YEARS, chartSeries: QuoteTimeRange.TWENTY_YEARS,
          frequencies: 'MONTHLY', translationId: 'tdWb.markets.quote.timePeriod.TWENTY_YEARS' }
      ],
      others: [
        { type: QuoteTimeRange.DAILY, chartSeries: QuoteTimeRange.INTRADAY,
          frequencies: 'DAILY', translationId: 'tdWb.markets.quote.timePeriod.DAILY' },
        { type: QuoteTimeRange.FIVE_DAYS, chartSeries: QuoteTimeRange.FIVE_DAYS,
          frequencies: 'DAILY', translationId: 'tdWb.markets.quote.timePeriod.FIVE_DAYS'  },
        { type: QuoteTimeRange.MONTHLY, chartSeries: QuoteTimeRange.MONTHLY,
          frequencies: 'WEEKLY', translationId: 'tdWb.markets.quote.timePeriod.MONTHLY' },
        { type: QuoteTimeRange.THREE_MONTHS, chartSeries: QuoteTimeRange.THREE_MONTHS,
          frequencies: 'WEEKLY', translationId: 'tdWb.markets.quote.timePeriod.THREE_MONTHS' },
        { type: QuoteTimeRange.YEAR_TO_DATE, chartSeries: QuoteTimeRange.YEARLY,
          frequencies: 'WEEKLY', translationId: 'tdWb.markets.quote.timePeriod.YEAR_TO_DATE' },
        { type: QuoteTimeRange.YEARLY, chartSeries: QuoteTimeRange.YEARLY,
          frequencies: 'MONTHLY', translationId: 'tdWb.markets.quote.timePeriod.YEARLY' },
        { type: QuoteTimeRange.THREE_YEARS, chartSeries: QuoteTimeRange.THREE_YEARS,
          frequencies: 'MONTHLY', translationId: 'tdWb.markets.quote.timePeriod.THREE_YEARS' },
        { type: QuoteTimeRange.FIVE_YEARS, chartSeries: QuoteTimeRange.FIVE_YEARS,
          frequencies: 'MONTHLY', translationId: 'tdWb.markets.quote.timePeriod.FIVE_YEARS' },
        { type: QuoteTimeRange.TEN_YEARS, chartSeries: QuoteTimeRange.TEN_YEARS,
          frequencies: 'YEARLY', translationId: 'tdWb.markets.quote.timePeriod.TEN_YEARS' },
        { type: QuoteTimeRange.TWENTY_YEARS, chartSeries: QuoteTimeRange.TWENTY_YEARS,
          frequencies: 'YEARLY', translationId: 'tdWb.markets.quote.timePeriod.TWENTY_YEARS' }
      ]
    };

  static readonly DEFAULT_FREQUENCY_SELECTOR: {[key: string]: Array<FrequencySelector>} = {
    DAILY: [
      { timePeriod: QuoteTimePeriod.MINUTE, interval: 1, translationId: 'tdWb.markets.quote.frequency.ONE_MIN' },
      { timePeriod: QuoteTimePeriod.MINUTE, interval: 5, translationId: 'tdWb.markets.quote.frequency.FIVE_MIN' },
      { timePeriod: QuoteTimePeriod.MINUTE, interval: 15, translationId: 'tdWb.markets.quote.frequency.FIFTEEN_MIN' },
      { timePeriod: QuoteTimePeriod.MINUTE, interval: 30, translationId: 'tdWb.markets.quote.frequency.THIRTY_MIN' },
      { timePeriod: QuoteTimePeriod.MINUTE, interval: 60, translationId: 'tdWb.markets.quote.frequency.ONE_HR' }
    ],
    TEN_DAYS: [
      { timePeriod: QuoteTimePeriod.MINUTE, interval: 5, translationId: 'tdWb.markets.quote.frequency.FIVE_MIN' },
      { timePeriod: QuoteTimePeriod.MINUTE, interval: 15, translationId: 'tdWb.markets.quote.frequency.FIFTEEN_MIN' },
      { timePeriod: QuoteTimePeriod.MINUTE, interval: 30, translationId: 'tdWb.markets.quote.frequency.THIRTY_MIN' },
      { timePeriod: QuoteTimePeriod.MINUTE, interval: 60, translationId: 'tdWb.markets.quote.frequency.ONE_HR' }
    ],
    WEEKLY: [
      { timePeriod: QuoteTimePeriod.DAY, interval: 1, translationId: 'tdWb.markets.quote.frequency.DAY' },
      { timePeriod: QuoteTimePeriod.WEEK, interval: 1, translationId: 'tdWb.markets.quote.frequency.WEEK' }
    ],
    MONTHLY: [
      { timePeriod: QuoteTimePeriod.DAY, interval: 1, translationId: 'tdWb.markets.quote.frequency.DAY' },
      { timePeriod: QuoteTimePeriod.WEEK, interval: 1, translationId: 'tdWb.markets.quote.frequency.WEEK' },
      { timePeriod: QuoteTimePeriod.MONTH, interval: 1, translationId: 'tdWb.markets.quote.frequency.MONTH' }
    ],
    YEARLY: [
      { timePeriod: QuoteTimePeriod.WEEK, interval: 1, translationId: 'tdWb.markets.quote.frequency.WEEK' },
      { timePeriod: QuoteTimePeriod.MONTH, interval: 1, translationId: 'tdWb.markets.quote.frequency.MONTH' }
    ]
  };

  private static readonly quoteLinks: {[key: string]: Array<QuoteLinks>} = {
    [QuoteIssueType.STOCK]: [
      { title : 'tdWb.markets.quote.OVERVIEW', link: 'link_markets_research_stock' },
      { title : 'tdWb.markets.quote.NEWS', link: 'link_markets_research_stocks_news' },
      { title : 'tdWb.markets.quote.CHARTS', link: 'link_markets_research_charts' },
      { title : 'tdWb.markets.quote.OPTION_CHAINS', link: 'link_markets_research_options_widget' }
    ],
    [QuoteIssueType.ETF]: [
      { title : 'tdWb.markets.quote.SUMMARY', link: 'link_markets_research_etfs_summary' },
      { title : 'tdWb.markets.quote.PERFORMANCE', link: 'link_markets_research_etfs_performance' },
      { title : 'tdWb.markets.quote.CHARTS', link: 'link_markets_research_etfs_charts' },
      { title : 'tdWb.markets.quote.OPTION_CHAINS', link: 'link_markets_research_etfs_options' }
    ],
    [QuoteIssueType.INDEX]: [
      { title : 'tdWb.markets.quote.LIST_OF_MEMBERS', link: 'link_markets_research_members', symbolFrom: 'issueId' },
      { title : 'tdWb.markets.quote.CHARTS', link: 'link_markets_research_indices_charts', symbolFrom: 'issueId' },
      { title : 'tdWb.markets.quote.OPTION_CHAINS', link: 'link_markets_research_options_widget' }
    ],
    [QuoteIssueType.MF]: [
      { title : 'tdWb.markets.quote.SUMMARY', link: 'link_markets_research_mutualfunds_summary' },
      { title : 'tdWb.markets.quote.PERFORMANCE', link: 'link_markets_research_mutualfunds_performance' },
      { title : 'tdWb.markets.quote.HOLDINGS', link: 'link_markets_research_mutualfunds_holdings' },
      { title : 'tdWb.markets.quote.CHARTS', link: 'link_markets_research_mutualfunds_charts' }
    ],
    [QuoteIssueType.OPTION]: [
      { title : 'tdWb.markets.quote.OVERVIEW', link: 'link_markets_research_options_overview', symbolFrom: 'underlyingSymbol' },
      { title : 'tdWb.markets.quote.NEWS', link: 'link_markets_research_stocks_news', symbolFrom: 'underlyingSymbol' },
      { title : 'tdWb.markets.quote.CHARTS', link: 'link_markets_research_charts', symbolFrom: 'underlyingSymbol' },
      { title : 'tdWb.markets.quote.OPTION_CHAINS', link: 'link_markets_research_etfs_options', symbolFrom: 'underlyingSymbol' }
    ]
  };

  static getQuoteLinks(issueType: QuoteIssueType): Array<QuoteLink> {
    const links = QuoteConstants.quoteLinks[issueType];
    if (links !== undefined) {
      return links.map((x: QuoteLink): QuoteLink => Object.assign({}, x));
    }
    return [];
  }
}
